/**
 * useDirtyState Hook
 * Tracks whether form data has changed from its original state
 * Used to enable/disable save buttons and prevent unnecessary API calls
 */

import { useRef, useCallback, useMemo } from 'react';

/**
 * Deep comparison using JSON serialization
 * Works for objects, arrays, primitives
 */
function deepEqual<T>(a: T, b: T): boolean {
    // Handle null/undefined
    if (a === b) return true;
    if (a == null || b == null) return false;

    // Use JSON comparison for deep equality
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return false;
    }
}

interface UseDirtyStateResult<T> {
    /** Whether the current value differs from the original */
    isDirty: boolean;
    /** Call this when entering edit mode to capture the original state */
    setOriginal: (value: T) => void;
    /** Call this to reset (e.g., after successful save or cancel) */
    reset: () => void;
}

/**
 * Hook to track dirty state for form editing
 *
 * @param currentValue - The current form value being edited
 * @returns Object with isDirty boolean and control functions
 *
 * @example
 * const [editedData, setEditedData] = useState(initialData);
 * const { isDirty, setOriginal, reset } = useDirtyState(editedData);
 *
 * // When entering edit mode:
 * useEffect(() => {
 *   if (isEditing) {
 *     setOriginal(initialData);
 *   }
 * }, [isEditing]);
 *
 * // Save button:
 * <button disabled={!isDirty || isSaving}>Save</button>
 */
export function useDirtyState<T>(currentValue: T): UseDirtyStateResult<T> {
    const originalRef = useRef<T | null>(null);

    const setOriginal = useCallback((value: T) => {
        // Deep clone to prevent reference issues
        try {
            originalRef.current = JSON.parse(JSON.stringify(value));
        } catch {
            originalRef.current = value;
        }
    }, []);

    const reset = useCallback(() => {
        originalRef.current = null;
    }, []);

    const isDirty = useMemo(() => {
        // If no original set yet, not dirty
        if (originalRef.current === null) return false;

        return !deepEqual(currentValue, originalRef.current);
    }, [currentValue]);

    return { isDirty, setOriginal, reset };
}

export default useDirtyState;
